import { useEffect, useState } from 'react';

export interface AudioDeviceOption {
  id: string;
  name: string;
  isDefault?: boolean;
}

// Global audio settings
export const audioSettings = {
  useDefaults: true,
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

// Setter functions for audio settings
export const setUseAudioDefaults = (value: boolean): void => {
  audioSettings.useDefaults = value;
};

export const setEchoCancellation = (value: boolean): void => {
  audioSettings.echoCancellation = value;
};

export const setNoiseSuppression = (value: boolean): void => {
  audioSettings.noiseSuppression = value;
};

export const setAutoGainControl = (value: boolean): void => {
  audioSettings.autoGainControl = value;
};

// We need to track selected devices since browser APIs aren't always reliable for retrieval
let currentInputDeviceId: string | null = localStorage.getItem('selectedInputDeviceId');
let currentOutputDeviceId: string | null = localStorage.getItem('selectedOutputDeviceId');

export const getUserMediaAudio = async (
  specificDeviceId?: string,
  sampleRate?: number
): Promise<MediaStream> => {
  console.log("[AudioDevices] getUserMediaAudio called with deviceId:", specificDeviceId, "sampleRate:", sampleRate);
  try {
    // Build constraints based on settings and device ID
    const audioConstraints = audioSettings.useDefaults
      ? (specificDeviceId ? { deviceId: { exact: specificDeviceId }, sampleRate } : { sampleRate })
      : {
        deviceId: specificDeviceId ? { exact: specificDeviceId } : undefined,
        echoCancellation: audioSettings.echoCancellation,
        noiseSuppression: audioSettings.noiseSuppression,
        autoGainControl: audioSettings.autoGainControl,
        sampleRate,
      };

    const constraints = { audio: audioConstraints };

    console.log('[AudioDevices] Using audio constraints:', constraints);
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    return stream;
  } catch (error) {
    console.error('[AudioDevices] Error accessing user media:', error);
    throw error;
  }
}

// Leave here for now...
export async function detectCurrentInputDeviceId(): Promise<string | null> {
  let stream: MediaStream | null = null;

  try {
    // 1️⃣ Open any mic (prompts only on the first call)
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const track = stream.getAudioTracks()[0];
    if (!track) return null;

    // deviceId comes from getSettings(); label comes from the track
    const { deviceId: rawId } = track.getSettings();
    const label = track.label;

    console.log("[AudioDevices] detectCurrentInputDeviceId: ", rawId, label);

    if (rawId && rawId !== "default") return rawId;

    // 2️⃣ Fallback: match the label to enumerateDevices()
    const devices = await navigator.mediaDevices.enumerateDevices();
    const entry = devices.find(
      d => d.kind === "audioinput" && d.label === label
    );

    return entry?.deviceId ?? null;
  } catch (err) {
    console.error("[AudioDevices] detectCurrentInputDeviceId:", err);
    return null;
  } finally {
    // Always close the mic so it releases OS resources
    stream?.getTracks().forEach(t => t.stop());
  }
}

// Function to set the currently active input device ID
export const setCurrentInputDeviceId = (deviceId: string | null): void => {
  currentInputDeviceId = deviceId;
  // Save to local storage for persistence across page reloads
  if (currentInputDeviceId) {
    localStorage.setItem('selectedInputDeviceId', currentInputDeviceId);
  } else {
    localStorage.removeItem('selectedInputDeviceId');
  }
};

// Function to get the currently active input device ID
export const getCurrentInputDeviceId = (): string | null => {
  // If we don't have a current input device ID in memory, try to get it from local storage
  if (!currentInputDeviceId) {
    currentInputDeviceId = localStorage.getItem('selectedInputDeviceId');
  }
  return currentInputDeviceId;
};

// Function to set the currently active output device ID
export const setCurrentOutputDeviceId = (deviceId: string | null): void => {
  currentOutputDeviceId = deviceId;
  // Save to local storage for persistence across page reloads
  if (currentOutputDeviceId) {
    localStorage.setItem('selectedOutputDeviceId', currentOutputDeviceId);
  } else {
    localStorage.removeItem('selectedOutputDeviceId');
  }
};

// Function to get the currently active output device ID
export const getCurrentOutputDeviceId = (): string | null => {
  // If we don't have a current output device ID in memory, try to get it from local storage
  if (!currentOutputDeviceId) {
    currentOutputDeviceId = localStorage.getItem('selectedOutputDeviceId');
  }
  return currentOutputDeviceId;
};

/**
 * Fetches all available audio input devices (microphones)
 * @returns Promise that resolves to an array of AudioDeviceOption objects
 */
export const getAudioInputDevices = async (): Promise<AudioDeviceOption[]> => {
  try {
    // // Request permission to access media devices
    // await getUserMediaAudio();

    const permission = await navigator.permissions.query({ name: "microphone" as PermissionName, });
    if (permission.state !== 'granted') {
      await navigator.mediaDevices.getUserMedia({ audio: true });
    }

    // Enumerate all media devices
    const devices = await navigator.mediaDevices.enumerateDevices();

    // Filter for audio input devices and map to our interface
    const audioInputDevices = devices
      .filter(device => device.kind === 'audioinput')
      .map(device => ({
        id: device.deviceId,
        name: device.label || `Microphone (${device.deviceId.substring(0, 8)}...)`,
        isDefault: device.deviceId === 'default'
      }));

    // If no default is marked, mark the first device as default
    if (audioInputDevices.length > 0 && !audioInputDevices.some(device => device.isDefault)) {
      audioInputDevices[0].isDefault = true;
    }

    return audioInputDevices;
  } catch (error) {
    console.error('[AudioDevices] Error fetching audio input devices:', error);
    return [];
  }
};

/**
 * Fetches all available audio output devices (speakers)
 * @returns Promise that resolves to an array of AudioDeviceOption objects
 */
export const getAudioOutputDevices = async (): Promise<AudioDeviceOption[]> => {
  try {
    // Request permission to access media devices
    await getUserMediaAudio();

    // Enumerate all media devices
    const devices = await navigator.mediaDevices.enumerateDevices();

    // Filter for audio output devices and map to our interface
    const audioOutputDevices = devices
      .filter(device => device.kind === 'audiooutput')
      .map(device => ({
        id: device.deviceId,
        name: device.label || `Speaker (${device.deviceId.substring(0, 8)}...)`,
        isDefault: device.deviceId === 'default'
      }));

    // If no default is marked, mark the first device as default
    if (audioOutputDevices.length > 0 && !audioOutputDevices.some(device => device.isDefault)) {
      audioOutputDevices[0].isDefault = true;
    }

    return audioOutputDevices;
  } catch (error) {
    console.error('[AudioDevices] Error fetching audio output devices:', error);
    return [];
  }
};

/**
 * Custom hook for accessing and managing audio devices
 * @returns Object containing audio input and output devices and loading state
 */
export const useAudioDevices = () => {
  const [audioInputDevices, setAudioInputDevices] = useState<AudioDeviceOption[]>([]);
  const [audioOutputDevices, setAudioOutputDevices] = useState<AudioDeviceOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadDevices = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Add device change listener
        navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

        // Initial fetch of devices
        await fetchDevices();

        setIsLoading(false);
      } catch (err) {
        setError('Failed to load audio devices');
        setIsLoading(false);
        console.error('[AudioDevices] Error in useAudioDevices hook:', err);
      }
    };

    const fetchDevices = async () => {
      const inputDevices = await getAudioInputDevices();
      const outputDevices = await getAudioOutputDevices();

      setAudioInputDevices(inputDevices);
      setAudioOutputDevices(outputDevices);
    };

    const handleDeviceChange = async () => {
      // When devices change, refresh our list
      await fetchDevices();
    };

    loadDevices();

    // Cleanup
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, []);

  return {
    audioInputDevices,
    audioOutputDevices,
    isLoading,
    error,
  };
};
